import { Trash2 } from 'lucide-react';
import { forwardRef, useState, type CSSProperties } from 'react';

import { Button } from '@/components/Button/Button';

import { formatRelativeAnnotationTime } from './annotationTime';
import {
  ANNOTATION_TEXT_COLOR_VALUES,
  DEFAULT_ANNOTATION_TEXT_COLOR,
  type Annotation,
  type AnnotationTextColor,
} from './annotationTypes';
import { NoteEditor } from './NoteEditor';
import styles from './InlineNoteCard.module.css';

interface InlineNoteCardProps {
  annotation: Annotation;
  author?: { avatar?: string; name: string } | null;
  highlightColor: string;
  isActive: boolean;
  isEditing: boolean;
  noteTextColor?: AnnotationTextColor;
  onCancelEdit: () => void;
  onDelete: (annotationId: string) => Promise<void> | void;
  onEdit: (annotationId: string) => void;
  onSave: (note: string) => Promise<void> | void;
  replies?: Annotation[];
  top: number;
}

export const InlineNoteCard = forwardRef<HTMLButtonElement, InlineNoteCardProps>(
  function InlineNoteCard(
    {
      annotation,
      author = null,
      highlightColor,
      isActive,
      isEditing,
      noteTextColor = DEFAULT_ANNOTATION_TEXT_COLOR,
      onCancelEdit,
      onDelete,
      onEdit,
      onSave,
      replies = [],
      top,
    },
    ref,
  ) {
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const resolvedTextColor = annotation.textColor ?? noteTextColor;

    return (
      <div
        className={styles.card}
        data-active={isActive ? 'true' : 'false'}
        data-note-theme={resolvedTextColor === 'white' ? 'dark' : 'light'}
        style={
          {
            '--annotation-highlight-color': highlightColor,
            '--annotation-note-text-color':
              ANNOTATION_TEXT_COLOR_VALUES[resolvedTextColor],
            top,
          } as CSSProperties
        }
      >
        {isEditing ? (
          <NoteEditor
            cancelLabel="Cancel"
            initialValue={annotation.note}
            onCancel={onCancelEdit}
            onSave={onSave}
            saveLabel="Update note"
          />
        ) : isConfirmingDelete ? (
          <div className={styles.confirmation}>
            <p className={styles.confirmationTitle}>Delete this note?</p>
            <div className={styles.confirmationActions}>
              <Button
                onClick={() => setIsConfirmingDelete(false)}
                size="small"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                destructive
                onClick={() => {
                  void onDelete(annotation.id);
                }}
                size="small"
                variant="secondary"
              >
                Delete
              </Button>
            </div>
          </div>
        ) : (
          <>
            <button
              className={styles.body}
              onClick={() => onEdit(annotation.id)}
              ref={ref}
              type="button"
            >
              <p className={styles.note}>{annotation.note}</p>
              <div className={styles.meta}>
                {author ? (
                  <span className={styles.author}>{author.name}</span>
                ) : null}
                <span className={styles.timestamp}>
                  {formatRelativeAnnotationTime(annotation.timestamp)}
                </span>
                {replies.length > 0 ? (
                  <span className={styles.replyCount}>
                    {replies.length} replies
                  </span>
                ) : null}
              </div>
            </button>

            <button
              aria-label="Delete note"
              className={styles.deleteButton}
              onClick={() => setIsConfirmingDelete(true)}
              type="button"
            >
              <Trash2 aria-hidden="true" size={14} />
            </button>
          </>
        )}
      </div>
    );
  },
);
